/**
 * Turn Indicator
 *
 * Displays the current half, turn number, and acting team in the replay header.
 * Shown on the right side of the minimal-chrome header.
 */
interface TurnIndicatorProps {
  half: 1 | 2 | "et"
  turnLabel: string | null
  actingTeamId: string | null
  homeTeamId: string
  awayTeamId: string
  homeTeamHue?: number
  awayTeamHue?: number
}

export function TurnIndicator({
  half,
  turnLabel,
  actingTeamId,
  homeTeamId,
  awayTeamId,
  homeTeamHue = 145,
  awayTeamHue = 275,
}: TurnIndicatorProps) {
  // Determine which team is acting
  const teamLabel =
    actingTeamId === homeTeamId
      ? "Home"
      : actingTeamId === awayTeamId
        ? "Away"
        : null

  const teamColor =
    actingTeamId === homeTeamId
      ? `oklch(0.50 0.15 ${homeTeamHue})`
      : actingTeamId === awayTeamId
        ? `oklch(0.48 0.15 ${awayTeamHue})`
        : null

  return (
    <div
      className="flex items-center gap-2 px-3 py-1 text-xs font-semibold tracking-wide uppercase"
      style={{
        background: "oklch(0.10 0.005 230)",
        border: "1px solid oklch(0.25 0.01 230 / 0.5)",
        color: "oklch(0.65 0.01 230)",
        minWidth: "140px",
        justifyContent: "center",
      }}
      data-testid="turn-indicator"
    >
      {/* Half badge */}
      <span
        className="px-1.5 py-0.5 text-[9px] font-black tracking-wider"
        style={{
          background: "oklch(0.20 0.01 230 / 0.5)",
          color: "oklch(0.80 0.16 85)",
        }}
      >
        {half === "et" ? "ET" : `H${half}`}
      </span>

      {/* Turn label */}
      <span style={{ color: "oklch(0.75 0.01 230)" }}>
        {turnLabel ?? "Kickoff"}
      </span>

      {/* Acting team dot + label */}
      {teamLabel && (
        <>
          <span style={{ color: "oklch(0.40 0.01 230)" }}>·</span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: teamColor ?? "transparent" }}
            />
            <span style={{ color: "oklch(0.85 0.01 230)" }}>{teamLabel}</span>
          </span>
        </>
      )}
    </div>
  )
}
